<template>
  <header v-if="showNavbar">
    <nav class="nav-bar">
      <div class="nav-bar__main" :class="{ 'is-white-label': customLogo }">
        <div class="nav-bar__header">
          <router-link
            v-if="showWhitelabelItem"
            data-cy="home-link"
            class="nav-bar__brand"
            to="/"
          >
            <div
              v-if="customLogo"
              class="logo custom-logo"
              data-cy="nav-custom-logo"
            >
              <img class="logo__img" :src="customLogo" />
            </div>
            <div v-else class="logo logo--samknows" data-cy="nav-sk-logo">
              <img
                class="logo__img"
                :src="`${publicPath}img/logo.svg`"
                :alt="$t('common.altSkLogo')"
              />
            </div>
          </router-link>

          <button
            v-show="hasSidebar"
            type="button"
            class="nav-bar__menu-toggle"
            @click="toggleMenu()"
          >
            <SvgIcon name="menu" />
          </button>
        </div>

        <div class="nav-bar__nav u-flex">
          <ul v-if="authed" key="authed" class="nav-bar__menus">
            <li v-if="!volunteer">
              <ul
                v-if="pinnedApps.length"
                data-tracking="navbar-pinned"
                class="nav-bar__pinned use-hover"
              >
                <li
                  v-for="app in pinnedApps"
                  :key="app.id"
                  class="nav__link-wrapper"
                >
                  <a
                    v-if="app.link.includes('//')"
                    class="nav__link"
                    :data-cy="`nav-link-${app.id}`"
                    :href="app.link"
                    target="_blank"
                  >
                    <span
                      class="link__icon"
                      :class="app.icon"
                      aria-hidden="true"
                    ></span>
                    <div class="link__text">
                      <span class="link__text-head">{{ app.name }}</span>
                      <span class="link__text-sub">{{ app.description }}</span>
                    </div>
                  </a>

                  <router-link
                    v-else
                    :to="app.link"
                    class="nav__link"
                    :class="{
                      'router-link-active': $route.path.startsWith(
                        app.activeLink
                      )
                    }"
                    :data-cy="`nav-link-${app.id}`"
                  >
                    <span
                      class="link__icon"
                      :class="app.icon"
                      aria-hidden="true"
                    ></span>
                    <div class="link__text">
                      <span class="link__text-head">{{ app.name }}</span>
                      <span class="link__text-sub">{{ app.description }}</span>
                    </div>
                  </router-link>
                </li>
              </ul>
            </li>

            <li
              v-if="!volunteer && notPinnedApps.length"
              class="drop-link-wrapper"
            >
              <button
                class="unstyled-button nav-bar__drop-link"
                data-cy="navbar-app-menu-btn"
                @click.stop="toggleAppMenu()"
                @dragover="toggleAppMenu(true)"
              >
                <span class="sk-icon-grid sk-icon-lg" aria-hidden="true"></span>
              </button>

              <DropdownBubble
                :is-visible="appMenuOpen"
                :vertical-offset="{ direction: 'top', value: '75px' }"
                px-wrapper-right="-19px"
                pointer-orientation="top"
                px-pointer-position="32px"
                @close="appMenuOpen = false"
              >
                <ul
                  class="nav-bar__not-pinned use-hover"
                  :class="{
                    'nav-bar__not-pinned--grid': notPinnedApps.length > 8
                  }"
                  data-cy="app-menu-list"
                >
                  <li
                    v-for="app in notPinnedApps"
                    :key="app.id"
                    class="nav__link-wrapper"
                  >
                    <NavLink :link="app" />
                  </li>
                </ul>

                <div
                  v-if="activeView.type !== 'units'"
                  class="dnd-instructions"
                >
                  {{ $t('navigation.dndInstructions') }}
                </div>
              </DropdownBubble>
            </li>

            <li v-if="!volunteer" class="drop-link-wrapper">
              <button
                v-tooltip:bottom-center="$t('navigation.accountIconTooltip')"
                class="unstyled-button nav-bar__drop-link"
                data-cy="sk-account-switcher"
                @click.stop="toggleAccountSwitcher"
              >
                <SvgIcon
                  class="nav-bar__icon"
                  name="user"
                  aria-hidden="true"
                  size="md"
                />
              </button>
              <AccountSwitcher
                :display="showAccountSwitcher"
                pointer-orientation="top"
                :vertical-offset="{ direction: 'top', value: '75px' }"
                px-wrapper-right="0px"
                px-pointer-position="18px"
                @close="showAccountSwitcher = false"
              />
            </li>

            <li v-if="volunteer">
              <ul class="nav-bar__pinned">
                <li v-if="showWhitelabelItem" class="nav__link-wrapper">
                  <NavLink :link="faqLink" />
                </li>
                <li class="nav__link-wrapper">
                  <LogoutButton class="nav__link" data-cy="nav-link-logout" />
                </li>
                <li class="nav__link-wrapper">
                  <a class="nav__link" @click="showLanguageSwitcher">
                    <SvgIcon
                      class="u-margin-right-2x"
                      name="chevron-down"
                      aria-hidden="true"
                    />
                    {{ activeLocale.name }}
                  </a>
                </li>
              </ul>
            </li>
          </ul>

          <ul
            v-else-if="showNotAuthedNav"
            key="not-authed"
            class="nav-bar__menus nav-bar__pinned"
            data-cy="not-authed-nav"
          >
            <li v-if="showWhitelabelItem" class="nav__link-wrapper">
              <NavLink :link="faqLink" />
            </li>
            <li data-cy="sk-language-switcher">
              <a class="nav-bar__language-link" @click="showLanguageSwitcher">
                <SvgIcon
                  class="u-margin-right-2x"
                  name="chevron-down"
                  aria-hidden="true"
                />
                {{ activeLocale.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
    <GlobalEvents target="window" @resize="handleResize" />
  </header>
</template>

<script>
import Vue, { computed, defineComponent, onBeforeMount, ref, watch } from 'vue';
import AccountSwitcher from '@/common/components/AccountSwitcher.vue';
import DropdownBubble from '@/common/components/DropdownBubble.vue';
import NavLink from '@/common/components/NavLink.vue';
import config from '@/common/config';
import { useWhiteLabelling } from '@/common/composables/whitelabel';
import { prefStorage } from '@samknows/utils';
import { SvgIcon } from '@samknows/design-system';
import viewTypes from '@/common/helpers/view-types';
import { heapTrack } from '../services/heapTracking';
import { isVolunteer } from '../services/account/user';
import LogoutButton from '@/common/components/LogoutButton.vue';
import { useRoute } from 'vue-router/composables';
import { DEFAULT_FAQS_LINK } from '@/common/constants';

export default defineComponent({
  components: {
    AccountSwitcher,
    DropdownBubble,
    NavLink,
    SvgIcon,
    LogoutButton
  },
  props: {
    authed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { getWhitelabelValue } = useWhiteLabelling();
    const customLogo = ref(null);
    const faqsUrl = ref(null);
    const route = useRoute();

    // Hide whitelabelled item when validating the user session as user might not be set yet and we can't use the right branding
    const showWhitelabelItem = computed(() => {
      return route.name !== 'ValidateSession';
    });

    watch(route, () => {
      customLogo.value = getWhitelabelValue('samknows_one_logo');
      faqsUrl.value = getWhitelabelValue('faqs') || DEFAULT_FAQS_LINK;
    });

    onBeforeMount(() => {
      customLogo.value = getWhitelabelValue('samknows_one_logo');
      faqsUrl.value = getWhitelabelValue('faqs') || DEFAULT_FAQS_LINK;
    });

    return { customLogo, faqsUrl, showWhitelabelItem };
  },
  data: () => ({
    showAccountSwitcher: false,
    volunteer: isVolunteer(),
    activeView: viewTypes.activeView,
    appMenuOpen: false,
    isMobile: window.innerWidth < 992,
    pinnedApps: [],
    notPinnedApps: [],
    publicPath: process.env.BASE_URL
  }),
  computed: {
    activeLocale() {
      return config.locales.find((locale) => locale.code === Vue.config.lang);
    },
    menuOpen() {
      return this.$store.state.menus.menuOpen;
    },
    hasSidebar() {
      return this.$store.state.menus.hasSidebar;
    },
    hideSidebar() {
      return this.$store.state.menus.hideSidebar;
    },
    showNotAuthedNav() {
      return !this.authed && !this.$route.meta.hideNotAuthedNav;
    },
    faqLink() {
      return {
        id: 'faqs',
        name: this.$t('navigation.faqs'),
        icon: 'sk-icon-faqs1',
        link: this.faqsUrl,
        description: this.$t('navigation.faqsSubText'),
        isDefaultPinned: false,
        hasPermission: true
      };
    },
    showNavbar() {
      return this.$route.query.navbar !== 'false';
    }
  },
  watch: {
    $route() {
      this.init();
    }
  },
  mounted() {
    this.init();

    const pinnedLinks = this.pinnedApps.map((item) => item.name);
    const notPinnedLinks = this.notPinnedApps.map((item) => item.name);
    heapTrack('NavbarLoadLinks', {
      pinnedLinksTotal: pinnedLinks.length,
      notPinnedLinksTotal: notPinnedLinks.length,
      pinnedLinks: pinnedLinks.join(', '),
      notPinnedLinks: notPinnedLinks.join(', ')
    });
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 992;

      this.init();
    },
    init() {
      if (!this.authed) {
        return;
      }

      this.pinnedApps = [];
      this.notPinnedApps = [];

      this.volunteer = isVolunteer();

      const appList = [
        {
          id: 'dashboard',
          name: this.$t('common.linkHealth'),
          icon: 'sk-icon-Dashboard-Icon',
          link: '/volunteers/dashboard',
          activeLink: '/volunteers/dashboard',
          isDefaultPinned: true,
          hasPermission: true
        },
        {
          id: 'analytics',
          name: this.$t('common.linkAnalytics'),
          icon: 'sk-icon-Analytics-Icon',
          link: '/volunteers/analytics',
          activeLink: '/volunteers/analytics',
          description: this.$t('navigation.analyticsSubText'),
          isDefaultPinned: true,
          hasPermission: true
        },
        this.faqLink
      ].filter((app) => app.hasPermission);

      if (this.isMobile) {
        this.notPinnedApps = appList;
        return;
      }

      if (appList.length < 4) {
        this.pinnedApps = appList;
        return;
      }

      function sortApps(orderArray) {
        const sortedApps = [];

        orderArray.forEach((id) => {
          const appIndex = appList.findIndex((app) => app.id === id);
          if (appIndex >= 0) {
            sortedApps.push(appList.splice(appIndex, 1)[0]);
          }
        });

        return sortedApps;
      }

      // Deal with legacy preferences
      const legacyPinnedOrder = prefStorage.get('pinned-order');
      if (legacyPinnedOrder) {
        prefStorage.set('apollo-pinned-order', legacyPinnedOrder, true, true);
        prefStorage.set('pinned-order', undefined, true, true);

        const legacyNotPinnedOrder = prefStorage.get('not-pinned-order');
        prefStorage.set(
          'apollo-not-pinned-order',
          legacyNotPinnedOrder,
          true,
          true
        );
        prefStorage.set('not-pinned-order', undefined, true, true);
      }

      const pinnedOrder = prefStorage.get(`${viewTypes.viewType}-pinned-order`);
      const notPinnedOrder = prefStorage.get(
        `${viewTypes.viewType}-not-pinned-order`
      );

      const newPinnedApps = pinnedOrder
        ? sortApps(pinnedOrder)
        : appList.filter((app) => app.isDefaultPinned);

      // Use splice
      this.pinnedApps.splice(0, this.pinnedApps.length, ...newPinnedApps);

      const newNotPinnedApps = notPinnedOrder
        ? sortApps(notPinnedOrder).concat(appList)
        : appList.filter((app) => !app.isDefaultPinned);

      this.notPinnedApps.splice(
        0,
        this.notPinnedApps.length,
        ...newNotPinnedApps
      );
    },
    showLanguageSwitcher() {
      this.$store.commit('languageSwitcher/SET_VISIBILITY', true);
    },
    toggleAccountSwitcher() {
      this.showAccountSwitcher = !this.showAccountSwitcher;

      if (this.showAccountSwitcher) {
        this.appMenuOpen = false;
      }
    },
    toggleAppMenu(open) {
      if (open && this.appMenuOpen) {
        return;
      }

      this.appMenuOpen = !this.appMenuOpen;

      if (this.appMenuOpen) {
        this.showAccountSwitcher = false;
      }
    },
    toggleMenu() {
      this.$store.commit('menus/toggleMenu');
    }
  }
});
</script>

<style lang="scss" scoped>
$height: rem-calc(60px);

header {
  height: $height;
}

.nav-bar {
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  color: color(primary-text);
  background: color(base-background);
  border-bottom: $border-normal;

  @include breakpoint(S) {
    display: flex;
  }
}

.nav-bar__header {
  display: flex;
}

.nav-bar__brand {
  .logo {
    height: $height;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }

  .logo__img {
    max-height: 100%;
  }

  .logo--samknows {
    height: $height;
    margin-top: 0;
    padding: 0 spacing('4x');

    @include breakpoint(extra-small) {
      padding: 0 rem-calc(5px);
    }

    .logo__img {
      min-width: rem-calc(80px);
    }
  }

  [dir='rtl'] & {
    border-right: none;
    border-left: rem-calc(1px) solid color(lines);
  }
}

.nav-bar__menu-toggle {
  display: block;

  height: $height;
  width: $height;
  padding: 0;

  border: 0;
  background: transparent;

  @include breakpoint(large) {
    display: none;
  }

  &:active {
    top: rem-calc(1px);
  }
}

.nav-bar__menus {
  display: flex;
  margin: 0;
  margin-bottom: rem-calc(-1px);
  padding: 0;
  white-space: nowrap;

  list-style: none;

  a {
    text-decoration: none;
  }

  ul {
    list-style: none;
    padding: 0;
  }
}

.dnd-instructions {
  padding: rem-calc(15px) rem-calc(20px);

  white-space: normal;
  text-align: center;
  color: color(primary-text);

  background: color('neutral-background');
  border-bottom-left-radius: rem-calc(5px);
  border-bottom-right-radius: rem-calc(5px);
}

.nav-bar__main {
  &.is-white-label {
    padding-left: 0;
  }

  display: flex;

  justify-content: space-between;
  flex: 1;

  padding: 0 rem-calc(10px);

  [dir='rtl'] & {
    padding: 0 0 0 rem-calc(10px);
  }
}

.nav-bar__drop-link {
  padding: 1em;

  height: $height;

  display: flex;
  align-items: center;
  color: color(primary-text);

  &:hover {
    color: inherit;
  }
}

.nav-bar__language-link {
  @include breakpoint(extra-small) {
    padding: 0;
  }
  padding: 1em;

  height: $height;

  display: flex;
  align-items: center;

  &:hover {
    color: inherit;
  }
}

.nav-bar__pinned {
  display: flex;

  :deep(.nav__link) {
    padding: 1em;

    height: $height;

    display: flex;
    align-items: center;
    border-bottom: rem-calc(3px) solid transparent;
    border-top: rem-calc(3px) solid transparent;
    color: color(navigation-default-text);

    &.router-link-active {
      font-weight: 600;
      color: color(navigation-selected-text);
    }

    &:hover {
      color: inherit;
    }
  }

  :deep(.link__icon),
  :deep(.link__text-sub) {
    display: none;
  }
}

.nav-bar__not-pinned {
  padding: rem-calc(15px);

  :deep(.nav__link) {
    display: flex;
    padding: rem-calc(8px);

    align-items: center;
    background: white;
    border: rem-calc(1px) solid transparent;

    color: color(navigation-default-text);

    transition: all 200ms ease-in-out;

    &.router-link-active {
      color: color(navigation-selected-text);
      background: color('navigation-selected-background');
    }
  }

  &.use-hover :deep(.nav__link:hover) {
    background: color(navigation-hover-background);
    color: color(navigation-selecting-text);
  }

  :deep(.link__icon) {
    margin: rem-calc(5px) 0;

    font-size: 2em;
  }

  :deep(.link__text) {
    width: 100%;
    margin: 0 rem-calc(15px);
  }

  :deep(.link__text-head) {
    display: block;

    margin: 0;

    font-size: 1.3em;
  }

  :deep(.link__text-sub) {
    display: block;

    margin-top: rem-calc(-3px);
    margin-bottom: 0;

    color: inherit;
  }
}

.nav-bar__not-pinned--grid {
  display: flex;
  flex-wrap: wrap;
  width: 80vw;
  max-width: rem-calc(280px);

  .nav__link-wrapper {
    width: 33.3%;
  }

  :deep(.nav__link) {
    flex-direction: column;
  }

  :deep(.link__icon) {
    font-size: 2.5em;
  }

  :deep(.link__text) {
    margin-top: rem-calc(5px);
  }

  :deep(.link__text-head) {
    font-size: 1em;
    text-overflow: ellipsis;
    text-align: center;

    overflow: hidden;
  }

  :deep(.link__text-sub) {
    display: none;
  }
}

.navbar-dragging .nav__link-wrapper {
  :deep(.link__text),
  :deep(.link__icon) {
    animation-name: wiggle;
    animation-duration: 0.2s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
    transform-origin: 50% 10%;
  }

  :deep(.link__text) {
    --rotate-from: -0.5deg;
    --rotate-to: 0.5deg;
  }

  :deep(.link__icon) {
    --rotate-from: 2deg;
    --rotate-to: -2deg;
  }

  &:nth-child(odd) {
    :deep(.link__text),
    :deep(.link__icon) {
      animation-delay: 0.1s;
    }
  }
}

.navbar-dragging .nav-bar__pinned :deep(.link__text) {
  --rotate-from: -1.5deg;
  --rotate-to: 1.5deg;
}

@keyframes wiggle {
  0%,
  100% {
    transform: rotate(var(--rotate-from));
  }

  50% {
    transform: rotate(var(--rotate-to));
  }
}

// Be careful when updating this style as it is responsible for positioning the the navbar on mobile
.drop-link-wrapper {
  position: relative;
}
</style>
